import * as Sentry from '@sentry/react'

const isDevelopment = import.meta.env.DEV
const environment = import.meta.env.VITE_ENVIRONMENT || (isDevelopment ? 'development' : 'production')

// Initialize Sentry
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN || 'https://6077d5fc74434b93b599c9425815cffa@o4507222095691776.ingest.de.sentry.io/4509631212945488',
  environment,
  
  // Performance monitoring
  tracesSampleRate: isDevelopment ? 1.0 : 0.1,
  
  // Session replay
  replaysSessionSampleRate: isDevelopment ? 1.0 : 0.1,
  replaysOnErrorSampleRate: 1.0,
  
  // Enable logging with different levels
  _experiments: {
    enableLogs: true,
  },
  
  integrations: [
    Sentry.replayIntegration(),
    
    // Browser tracing for performance monitoring
    Sentry.browserTracingIntegration({
      enableInp: true,
    }),
    
    // Console logging integration
    Sentry.consoleLoggingIntegration({
      levels: ['error', 'warn']
    }),
    
    // HTTP client integration for API calls
    Sentry.httpClientIntegration({
      failedRequestStatusCodes: [400, 500, 599],
    }),
  ],
  
  // Don't capture errors in development
  enabled: !isDevelopment,
  
  // Release tracking (you can set this from your CI/CD)
  release: import.meta.env.VITE_SENTRY_RELEASE || 'unknown',
  
  // Set user context automatically
  initialScope: {
    tags: {
      component: 'frontend',
    },
  },
  
  // Filter out noise
  beforeSend(event, hint) {
    // Filter out known development errors
    if (isDevelopment) {
      return null
    }
    
    // Filter out hot reload errors
    if (event.exception?.values?.some(
      exception => exception.value?.includes('Loading chunk') || 
                  exception.value?.includes('Loading CSS chunk')
    )) {
      return null
    }
    
    // Filter out auth context errors during hot reloads
    if (event.exception?.values?.some(
      exception => exception.value?.includes('useAuth') || 
                  exception.value?.includes('AuthProvider')
    )) {
      return null
    }
    
    return event
  },
  
  // Additional configuration
  maxBreadcrumbs: 100,
  attachStacktrace: true,
})

// Export logger for structured logging
export const logger = Sentry.logger

// Utility functions for common Sentry operations
export const sentryUtils = {
  // Set user context
  setUser: (user: { id: string; email?: string; username?: string }) => {
    Sentry.setUser(user)
  },
  
  // Clear user context (on logout)
  clearUser: () => {
    Sentry.setUser(null)
  },
  
  // Set additional context
  setContext: (key: string, context: Record<string, any>) => {
    Sentry.setContext(key, context)
  },
  
  // Add breadcrumb
  addBreadcrumb: (message: string, category?: string, data?: Record<string, any>) => {
    Sentry.addBreadcrumb({
      message,
      category: category || 'custom',
      data,
      level: 'info',
    })
  },
  
  // Capture custom error
  captureError: (error: Error, context?: Record<string, any>) => {
    if (context) {
      Sentry.withScope(scope => {
        Object.entries(context).forEach(([key, value]) => {
          scope.setContext(key, value)
        })
        Sentry.captureException(error)
      })
    } else {
      Sentry.captureException(error)
    }
  },
  
  // Capture custom message
  captureMessage: (message: string, level: 'info' | 'warning' | 'error' = 'info') => {
    Sentry.captureMessage(message, level)
  },
  
  // Start custom span for performance tracking
  startSpan: <T>(
    options: { op: string; name: string; attributes?: Record<string, any> },
    callback: (span: Sentry.Span) => T
  ): T => {
    return Sentry.startSpan(options, callback)
  },
}

// Re-export Sentry for direct usage
export { Sentry }
export default Sentry 